import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { CONSTANT } from '../utils/constants';
import { GlobalContext } from '../utils/GlobalContext';
import { isDiamondPriceHidden } from '../utils/helpers';

const TIERS = [
    {
        key: 'promotional',
        label: 'Best Value',
        badge: 'Promotional',
        badgeColor: '#28a745',
        colorRange: ['H', 'M'],
        clarity: ['SI1', 'SI2', 'I1', 'I2', 'I3'],
    },
    {
        key: 'midrange',
        label: 'Popular Choice',
        badge: 'Mid-Range',
        badgeColor: '#1a6fc4',
        colorRange: ['F', 'G'],
        clarity: ['VS1', 'VS2'],
    },
    {
        key: 'higherend',
        label: 'Premium',
        badge: 'Higher-End',
        badgeColor: '#b8860b',
        colorRange: ['D', 'E'],
        clarity: ['IF', 'VVS1', 'VVS2'],
    },
];

const PAGE_LIMIT = 3; // stones per tier per page when expanded

const AIRecommendedDiamonds = ({ account, shape = 'Round', openTab, onSelect, stoneSize, stoneType = 'all', certificateType = 'all', itemCode = '', selectedColors = [], selectedClarities = [] }) => {
    const { updateLayerParams, flags } = useContext(GlobalContext);
    const [stones, setStones] = useState({});
    const [fallbackStones, setFallbackStones] = useState({});
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [moreLoading, setMoreLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const isFetchingRef = useRef(false);
    const sentinelRef = useRef(null);

    const allColorLabels = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];
    const allClarityLabels = ['IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2', 'I3'];
    const isAllColors = selectedColors.length === allColorLabels.length;
    const isAllClarities = selectedClarities.length === allClarityLabels.length;

    const fetchTier = (tier, limit, pageNum) =>
        axios.post(`${CONSTANT.BASE_URL}api/Builder/all-diamonds`, {
            request_type: 'ajax',
            carat_size: stoneSize ? stoneSize : ['0.50', '3.00'],
            colorRange: tier.colorRange,
            clarity: tier.clarity,
            color_filter: isAllColors ? undefined : selectedColors,
            clarity_filter: isAllClarities ? undefined : selectedClarities,
            shape,
            search_diamond: '',
            natural_lab_filter: stoneType,
            certificateType,
            item_code_filter: itemCode,
            account: account || 'K200',
            page: pageNum,
            limit,
        }).then(res => ({
            key: tier.key,
            stones: res.data.data || [],
            total: res.data.total_diamonds_found || 0,
        }));

    const fetchFallbackForTier = (tier) =>
        axios.post(`${CONSTANT.BASE_URL}api/Builder/all-diamonds`, {
            request_type: 'ajax',
            carat_size: stoneSize ? stoneSize : ['0.50', '3.00'],
            shape,
            search_diamond: '',
            natural_lab_filter: stoneType,
            certificateType,
            item_code_filter: itemCode,
            account: account || 'K200',
            page: 1,
            limit: 1,
        }).then(res => ({
            key: tier.key,
            stones: res.data.data || [],
        }));

    // Initial load — 1 stone per tier
    useEffect(() => {
        setLoading(true);
        setExpanded(false);
        setPage(1);
        setHasMore(true);
        setFallbackStones({});
        isFetchingRef.current = false;
        Promise.all(TIERS.map(t => fetchTier(t, 1, 1)))
            .then(results => {
                const map = {};
                results.forEach(r => { map[r.key] = { stones: r.stones, total: r.total }; });
                setStones(map);

                // For tiers with no results, fetch a fallback diamond
                const emptyTiers = TIERS.filter(t => {
                    const result = results.find(r => r.key === t.key);
                    return !result || result.stones.length === 0;
                });
                if (emptyTiers.length > 0) {
                    Promise.all(emptyTiers.map(t => fetchFallbackForTier(t)))
                        .then(fbResults => {
                            const fbMap = {};
                            fbResults.forEach(r => { fbMap[r.key] = r.stones; });
                            setFallbackStones(fbMap);
                        })
                        .catch(() => {});
                }
            })
            .catch(err => console.error('AI Recommended Diamonds error:', err))
            .finally(() => setLoading(false));
    }, [account, shape, stoneSize, stoneType, certificateType, itemCode, selectedColors, selectedClarities]);

    // Load next page and append stones to each tier
    const loadNextPage = useCallback((nextPage) => {
        if (isFetchingRef.current || !hasMore) return;
        isFetchingRef.current = true;
        setMoreLoading(true);
        Promise.all(TIERS.map(t => fetchTier(t, PAGE_LIMIT, nextPage)))
            .then(results => {
                const anyNew = results.some(r => r.stones.length > 0);
                setStones(prev => {
                    const map = { ...prev };
                    results.forEach(r => {
                        map[r.key] = {
                            stones: [...(prev[r.key]?.stones || []), ...r.stones],
                            total: r.total,
                        };
                    });
                    return map;
                });
                setPage(nextPage);
                setHasMore(anyNew);
            })
            .catch(err => console.error('Pagination error:', err))
            .finally(() => {
                setMoreLoading(false);
                isFetchingRef.current = false;
            });
    }, [hasMore, account, shape, stoneSize, stoneType, certificateType, itemCode, selectedColors, selectedClarities]);

    // View More — expand to page 1 with PAGE_LIMIT
    const handleViewMore = () => {
        if (expanded) {
            // Show Less: collapse back to 1 per tier
            setMoreLoading(true);
            isFetchingRef.current = true;
            Promise.all(TIERS.map(t => fetchTier(t, 1, 1)))
                .then(results => {
                    const map = {};
                    results.forEach(r => { map[r.key] = { stones: r.stones, total: r.total }; });
                    setStones(map);
                    setExpanded(false);
                    setPage(1);
                    setHasMore(true);
                })
                .catch(err => console.error('Show less error:', err))
                .finally(() => {
                    setMoreLoading(false);
                    isFetchingRef.current = false;
                });
            return;
        }
        // Expand: fetch page 1 with PAGE_LIMIT
        setMoreLoading(true);
        isFetchingRef.current = true;
        Promise.all(TIERS.map(t => fetchTier(t, PAGE_LIMIT, 1)))
            .then(results => {
                const map = {};
                results.forEach(r => { map[r.key] = { stones: r.stones, total: r.total }; });
                setStones(map);
                setExpanded(true);
                setPage(1);
                setHasMore(results.some(r => r.stones.length >= PAGE_LIMIT));
            })
            .catch(err => console.error('View more error:', err))
            .finally(() => {
                setMoreLoading(false);
                isFetchingRef.current = false;
            });
    };

    // IntersectionObserver — auto-load next page when sentinel is visible
    useEffect(() => {
        if (!expanded || !sentinelRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isFetchingRef.current && hasMore) {
                    loadNextPage(page + 1);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [expanded, page, hasMore, loadNextPage]);

    const handleSelect = (stone) => {
        if (onSelect) {
            onSelect(stone);
        } else {
            updateLayerParams('Diamond Details', 'diamond_details', stone);
            updateLayerParams('Diamond Details', 'diamond_prices', stone?.prices);
            if (openTab) openTab('Layer2');
        }
    };

    const showToggle = expanded || TIERS.some(t => (stones[t.key]?.total || 0) > 1);

    return (
        <div className="ai-rec-wrapper">
            <div className="ai-rec-header">
                <span className="ai-rec-icon">✦</span>
                <span className="ai-rec-title">AI Recommended Diamonds</span>
            </div>

            {loading ? (
                <div className="ai-rec-loading">
                    <span className="ai-rec-spinner"></span>
                    Loading recommendations...
                </div>
            ) : (
                <>
                    <div className="row ai-rec-cards-row">
                        {TIERS.map(tier => {
                            const entry = stones[tier.key] || {};
                            const tierStones = entry.stones || [];

                            return (
                                <div key={tier.key} className="col-12 col-sm-4 col-md-4 col-lg-4 ai-rec-col">
                                    <div className="ai-rec-tier-label">
                                        <span className="ai-rec-badge" style={{ backgroundColor: tier.badgeColor }}>
                                            {tier.badge}
                                        </span>
                                        <span className="ai-rec-tier-sublabel">{tier.label}</span>
                                    </div>

                                    <div className="ai-rec-tier-cards">
                                        {(() => {
                                            const displayStones = tierStones.length > 0 ? tierStones : (fallbackStones[tier.key] || []);
                                            const isFallback = tierStones.length === 0 && displayStones.length > 0;
                                            if (displayStones.length === 0) {
                                                return (
                                                    <div className="diamond-card">
                                                        <div className="ai-rec-empty">No stones found in this range</div>
                                                    </div>
                                                );
                                            }
                                            return displayStones.map((stone, idx) => (
                                                <div key={stone.stock_num || idx} className="diamond-card ai-rec-card" style={idx > 0 ? { marginTop: '10px' } : {}}>
                                                    {isFallback && (
                                                        <div style={{ fontSize: '11px', color: '#888', padding: '4px 8px', background: '#f8f8f8', borderBottom: '1px solid #eee' }}>
                                                            Similar option
                                                        </div>
                                                    )}
                                                    <div className="diamond-card-body">
                                                        <div className="diamond-card-image">
                                                            {stone.video_link ? (
                                                                <video src={stone.video_link} autoPlay playsInline preload="auto" style={{ width: '100%' }} />
                                                            ) : (
                                                                <img src={stone.image_link} alt={stone.shape} />
                                                            )}
                                                        </div>
                                                        <div className="diamond-card-info">
                                                            <div className="diamond-card-title">
                                                                {stone.diamond_type} – {stone.shape} {stone.carat} ct
                                                            </div>
                                                            <div className="diamond-card-details">
                                                                <span>Color: <b>{stone.color}</b> | Clarity: <b>{stone.clarity}</b></span>
                                                                {stone.Cut && (
                                                                    <span>Cut: <b>{stone.Cut}</b>{stone.Polish ? ` | Polish: ${stone.Polish}` : ''}</span>
                                                                )}
                                                                {stone.Lab && stone.certificate && (
                                                                    <span className="diamond-card-certificate">{stone.Lab} #{stone.certificate}</span>
                                                                )}
                                                            </div>
                                                            {!isDiamondPriceHidden(flags) && (
                                                                <div className="diamond-card-price">
                                                                    {stone.display_pricing || stone.prices}
                                                                </div>
                                                            )}
                                                            <button className="diamond-card-add-btn" title="Select this diamond" onClick={() => handleSelect(stone)}>
                                                                +
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="diamond-card-footer">
                                                        {stone.stock_num || 'N/A'}
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Sentinel for infinite scroll — only active when expanded */}
                    {expanded && <div ref={sentinelRef} style={{ height: '1px' }} />}

                    {/* Loading indicator for auto-pagination */}
                    {moreLoading && expanded && (
                        <div className="ai-rec-loading" style={{ justifyContent: 'center' }}>
                            <span className="ai-rec-spinner"></span>
                            Loading more...
                        </div>
                    )}

                    {/* View More / Show Less button */}
                    {showToggle && (
                        <div className="ai-rec-view-more-wrap">
                            <button
                                className="ai-rec-view-more-btn"
                                onClick={handleViewMore}
                                disabled={moreLoading}
                            >
                                {moreLoading && !expanded ? 'Loading...' : expanded ? 'Show Less' : 'View More'}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AIRecommendedDiamonds;
